// © Microsoft Corporation. All rights reserved.
import { DOM, HTMLBindingDirective, HTMLDirective, } from "@microsoft/fast-element";
const noopBehavior = Object.freeze({
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    bind() { },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    unbind() { },
});
const noopFactory = Object.freeze({
    targetIndex: 0,
    createBehavior() {
        return noopBehavior;
    },
});
class TelemetryDirective extends HTMLDirective {
    constructor(metadataOrBinding, options) {
        super();
        this.metadataOrBinding = metadataOrBinding;
        this.options = options;
        this._factory = null;
    }
    get factory() {
        let factory = this._factory;
        if (factory === null) {
            this._factory = factory = this.selectFactory();
        }
        return factory;
    }
    createBehavior(target) {
        return this.factory.createBehavior(target);
    }
    createPlaceholder(index) {
        return DOM.createCustomAttributePlaceholder("fast-telemetry", index);
    }
    static installFactorySelector(factory) {
        TelemetryDirective.prototype["selectFactory"] = factory;
    }
    selectFactory() {
        return noopFactory;
    }
}
/**
 * @public
 * Helper used to set telemetry attributes on web components
 * @param metadataOrBinding - the telemetry value or a binding to get the value from
 * @param options - Options to pass to the underlying telemetry system
 */
export function telemetry(metadataOrBinding, options) {
    return new TelemetryDirective(metadataOrBinding, options);
}
class OneTimeAttributeBehavior {
    constructor(target, attribute, binding) {
        this.target = target;
        this.attribute = attribute;
        this.binding = binding;
    }
    bind(source, context) {
        DOM.setAttribute(this.target, this.attribute, this.binding(source, context));
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    unbind() { }
}
class OneTimeAttributeFactory {
    constructor(binding, attribute) {
        this.binding = binding;
        this.attribute = attribute;
        this.targetIndex = 0;
    }
    createBehavior(node) {
        return new OneTimeAttributeBehavior(node, this.attribute, this.binding);
    }
}
class StaticAttributeFactory {
    constructor(value, attribute) {
        this.value = value;
        this.attribute = attribute;
        this.targetIndex = 0;
    }
    createBehavior(node) {
        node.setAttribute(this.attribute, this.value);
        return noopBehavior;
    }
}
let telemetryOptions = {
    dynamicBindings: true,
    telemetryAttribute: "data-t",
};
/**
 * @public
 * Override the noop default behavior of the Fast telemetry directive
 * Should be run once per page session.
 */
export function initPageTelemetryBehavior(options = telemetryOptions) {
    telemetryOptions = options;
    TelemetryDirective.installFactorySelector(function () {
        const finalOptions = Object.assign({}, telemetryOptions, this.options);
        const metadataOrBinding = this.metadataOrBinding;
        if (typeof metadataOrBinding === "function") {
            if (finalOptions.dynamicBindings) {
                const bindingDirective = new HTMLBindingDirective(metadataOrBinding);
                bindingDirective.targetName = finalOptions.telemetryAttribute;
                return bindingDirective;
            }
            else {
                return new OneTimeAttributeFactory(metadataOrBinding, finalOptions.telemetryAttribute);
            }
        }
        return new StaticAttributeFactory(metadataOrBinding, options.telemetryAttribute);
    });
}
